export interface NutritionServiceApiLog {
  id: string;
  timestamp: number;
  functionName: string;
  requestedModel: string;
  actualModel: string;
  modelCategory: 'Gemini 3.7 Flash' | 'Gemini 1.5 Pro' | 'Other Models';
  status: 'success' | 'error';
  durationMs: number;
  errorMessage?: string;
  attemptCount: number;
}

export interface SummaryReportStats {
  timeWindowHours: number;
  totalCalls: number;
  gemini37Count: number;
  gemini37Percent: number;
  geminiProCount: number;
  geminiProPercent: number;
  otherModelsCount: number;
  otherModelsPercent: number;
  otherModelsBreakdown: Record<string, number>;
  successCount: number;
  errorCount: number;
  successRatePercent: number;
  avgDurationMs: number;
  functionBreakdown: Record<string, {
    total: number;
    gemini37: number;
    geminiPro: number;
    other: number;
    success: number;
    error: number;
    avgDurationMs: number;
  }>;
}

const STORAGE_KEY = 'nutrition_service_gemini_api_logs';
const MAX_LOG_AGE_MS = 48 * 60 * 60 * 1000; // Keep up to 48h in storage

let inMemoryLogs: NutritionServiceApiLog[] = [];

/**
 * Classifies a model string into Gemini 3.7 Flash, Gemini 1.5 Pro, or Other Models
 */
export function classifyGeminiModel(modelName: string): 'Gemini 3.7 Flash' | 'Gemini 1.5 Pro' | 'Other Models' {
  if (!modelName) return 'Other Models';
  const lower = modelName.toLowerCase().trim();
  if (lower.includes('3.7-flash') || lower.includes('3.7_flash') || lower === 'gemini-3.7-flash') {
    return 'Gemini 3.7 Flash';
  }
  if (lower.includes('1.5-pro') || lower.includes('1.5_pro') || lower === 'gemini-1.5-pro') {
    return 'Gemini 1.5 Pro';
  }
  return 'Other Models';
}

/**
 * Loads stored logs from localStorage with fallback to memory
 */
export function loadNutritionServiceLogs(): NutritionServiceApiLog[] {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: NutritionServiceApiLog[] = JSON.parse(stored);
        const cutoff = Date.now() - MAX_LOG_AGE_MS;
        const valid = parsed.filter(l => l && typeof l.timestamp === 'number' && l.timestamp >= cutoff);
        inMemoryLogs = valid;
        return valid;
      }
    }
  } catch (e) {
    console.warn('[NutritionInterceptor] Failed to load logs from localStorage:', e);
  }
  return inMemoryLogs;
}

/**
 * Saves logs to storage
 */
function saveNutritionServiceLogs(logs: NutritionServiceApiLog[]) {
  inMemoryLogs = logs;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(logs.slice(-500)));
    }
  } catch (e) {
    console.warn('[NutritionInterceptor] Failed to save logs to localStorage:', e);
  }
}

/**
 * Interceptor function: Records an API call specifically for nutritionService functions
 */
export function recordNutritionServiceApiCall(log: Omit<NutritionServiceApiLog, 'id' | 'timestamp' | 'modelCategory'>): NutritionServiceApiLog {
  const category = classifyGeminiModel(log.actualModel || log.requestedModel);
  const fullLog: NutritionServiceApiLog = {
    id: `nlog_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    timestamp: Date.now(),
    modelCategory: category,
    ...log
  };

  const logs = loadNutritionServiceLogs();
  logs.push(fullLog);
  saveNutritionServiceLogs(logs);

  // Output updated 24h summary report to console
  outputNutritionServiceApiSummaryReport(24 * 60 * 60 * 1000);

  return fullLog;
}

/**
 * Calculates summary report metrics for the specified time window (default 24h)
 */
export function calculateNutritionServiceSummaryStats(timeWindowMs: number = 24 * 60 * 60 * 1000): SummaryReportStats {
  const logs = loadNutritionServiceLogs();
  const cutoff = Date.now() - timeWindowMs;
  const recentLogs = logs.filter(l => l.timestamp >= cutoff);

  const totalCalls = recentLogs.length;
  let gemini37Count = 0;
  let geminiProCount = 0;
  let otherModelsCount = 0;
  const otherModelsBreakdown: Record<string, number> = {};
  let successCount = 0;
  let errorCount = 0;
  let totalDuration = 0;

  const functionBreakdown: SummaryReportStats['functionBreakdown'] = {};

  recentLogs.forEach(l => {
    const category = l.modelCategory || classifyGeminiModel(l.actualModel || l.requestedModel);
    if (category === 'Gemini 3.7 Flash') {
      gemini37Count++;
    } else if (category === 'Gemini 1.5 Pro') {
      geminiProCount++;
    } else {
      otherModelsCount++;
      const modelKey = l.actualModel || l.requestedModel || 'unknown';
      otherModelsBreakdown[modelKey] = (otherModelsBreakdown[modelKey] || 0) + 1;
    }

    if (l.status === 'success') successCount++;
    else errorCount++;

    totalDuration += (l.durationMs || 0);

    const fn = l.functionName || 'unspecified';
    if (!functionBreakdown[fn]) {
      functionBreakdown[fn] = {
        total: 0,
        gemini37: 0,
        geminiPro: 0,
        other: 0,
        success: 0,
        error: 0,
        avgDurationMs: 0
      };
    }
    const fnStats = functionBreakdown[fn];
    fnStats.total++;
    if (category === 'Gemini 3.7 Flash') fnStats.gemini37++;
    else if (category === 'Gemini 1.5 Pro') fnStats.geminiPro++;
    else fnStats.other++;

    if (l.status === 'success') fnStats.success++;
    else fnStats.error++;

    fnStats.avgDurationMs += (l.durationMs || 0);
  });

  // Calculate averages per function
  Object.keys(functionBreakdown).forEach(fn => {
    const stats = functionBreakdown[fn];
    stats.avgDurationMs = stats.total > 0 ? Math.round(stats.avgDurationMs / stats.total) : 0;
  });

  const timeWindowHours = Math.round(timeWindowMs / (60 * 60 * 1000));
  const gemini37Percent = totalCalls > 0 ? Number(((gemini37Count / totalCalls) * 100).toFixed(1)) : 0;
  const geminiProPercent = totalCalls > 0 ? Number(((geminiProCount / totalCalls) * 100).toFixed(1)) : 0;
  const otherModelsPercent = totalCalls > 0 ? Number(((otherModelsCount / totalCalls) * 100).toFixed(1)) : 0;
  const successRatePercent = totalCalls > 0 ? Number(((successCount / totalCalls) * 100).toFixed(1)) : 100;
  const avgDurationMs = totalCalls > 0 ? Math.round(totalDuration / totalCalls) : 0;

  return {
    timeWindowHours,
    totalCalls,
    gemini37Count,
    gemini37Percent,
    geminiProCount,
    geminiProPercent,
    otherModelsCount,
    otherModelsPercent,
    otherModelsBreakdown,
    successCount,
    errorCount,
    successRatePercent,
    avgDurationMs,
    functionBreakdown
  };
}

/**
 * Outputs a formatted summary report to the browser console for the last 24 hours (or specified window)
 */
export function outputNutritionServiceApiSummaryReport(timeWindowMs: number = 24 * 60 * 60 * 1000): SummaryReportStats {
  const stats = calculateNutritionServiceSummaryStats(timeWindowMs);
  const dateStr = new Date().toISOString().replace('T', ' ').substring(0, 19);

  console.groupCollapsed(`[NutritionService API Interceptor] 📊 Gemini Usage Summary Report (${stats.timeWindowHours}h) - ${dateStr}`);

  console.log(`================================================================================`);
  console.log(`📊 NUTRITION SERVICE GEMINI API USAGE REPORT (LAST ${stats.timeWindowHours} HOURS)`);
  console.log(`================================================================================`);
  console.log(`⏰ Report Timestamp : ${dateStr}`);
  console.log(`📦 Total Calls Logged: ${stats.totalCalls}`);
  console.log(``);
  console.log(`--- MODEL USAGE BREAKDOWN ---`);
  console.log(`⚡ Gemini 3.7 Flash : ${stats.gemini37Count} calls (${stats.gemini37Percent}%)`);
  console.log(`⚡ Gemini 1.5 Pro   : ${stats.geminiProCount} calls (${stats.geminiProPercent}%)`);
  console.log(`🎨 Other Models     : ${stats.otherModelsCount} calls (${stats.otherModelsPercent}%)`);
  
  if (Object.keys(stats.otherModelsBreakdown).length > 0) {
    Object.entries(stats.otherModelsBreakdown).forEach(([model, count]) => {
      console.log(`   └─ ${model}: ${count} call(s)`);
    });
  }

  console.log(``);
  console.log(`--- PERFORMANCE & SYSTEM HEALTH ---`);
  console.log(`✅ Successful Calls : ${stats.successCount} (${stats.successRatePercent}%)`);
  console.log(`❌ Failed Calls     : ${stats.errorCount}`);
  console.log(`⏱️ Avg Response Time: ${stats.avgDurationMs} ms`);
  console.log(``);
  console.log(`--- FUNCTION BREAKDOWN ---`);

  if (Object.keys(stats.functionBreakdown).length === 0) {
    console.log(`(No nutritionService calls recorded in the last ${stats.timeWindowHours} hours)`);
  } else {
    Object.entries(stats.functionBreakdown).forEach(([fnName, fnStats], idx) => {
      const sr = fnStats.total > 0 ? ((fnStats.success / fnStats.total) * 100).toFixed(0) : '0';
      console.log(`${idx + 1}. ${fnName}:`);
      console.log(`   • Total: ${fnStats.total} | 3.7 Flash: ${fnStats.gemini37} | 1.5 Pro: ${fnStats.geminiPro} | Other: ${fnStats.other} | Success Rate: ${sr}% | Avg: ${fnStats.avgDurationMs}ms`);
    });
  }

  console.log(`================================================================================`);
  
  if (stats.totalCalls > 0 && typeof console.table === 'function') {
    const tableData = Object.entries(stats.functionBreakdown).map(([fnName, s]) => ({
      'Function Name': fnName,
      'Total Calls': s.total,
      'Gemini 3.7 Flash': s.gemini37,
      'Gemini 1.5 Pro': s.geminiPro,
      'Other Models': s.other,
      'Success %': `${s.total > 0 ? Math.round((s.success / s.total) * 100) : 0}%`,
      'Avg Latency (ms)': `${s.avgDurationMs}ms`
    }));
    console.table(tableData);
  }

  console.groupEnd();

  return stats;
}

/**
 * Clears recorded logs
 */
export function clearNutritionServiceLogs() {
  inMemoryLogs = [];
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {}
  console.log('[NutritionInterceptor] Cleared all nutritionService API logs.');
}

// Attach helper functions to window for convenient browser console inspection
if (typeof window !== 'undefined') {
  (window as any).getNutritionServiceApiSummaryReport = calculateNutritionServiceSummaryStats;
  (window as any).outputNutritionServiceApiSummaryReport = outputNutritionServiceApiSummaryReport;
  (window as any).getNutritionServiceLogs = loadNutritionServiceLogs;
  (window as any).clearNutritionServiceLogs = clearNutritionServiceLogs;
  
  // Initial summary output on module load if logs exist
  try {
    const initialLogs = loadNutritionServiceLogs();
    if (initialLogs.length > 0) {
      outputNutritionServiceApiSummaryReport(24 * 60 * 60 * 1000);
    }
  } catch (e) {}
}
